import { AlertTriangle, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useClientSearchV2 } from '@/hooks/useClientSearchV2'
import { ApiError } from '@/lib/api/client'
import {
  getClientMergePreviewV2,
  mergeClientV2,
  type ClientSearchItemV2,
  type CustomerMergeIdentityV2,
  type CustomerMergePreviewV2,
} from '@/lib/api/v2/clients'
import { showSuccessToast } from '@/lib/toast'

interface MergeCustomerDialogProps {
  open: boolean
  sourceId: string
  sourceName: string
  onClose: () => void
}

const MERGE_FAILED = "La fusion n'a pas pu se faire. Rechargez la page et réessayez."
const NO_PROJECT_ACCESS = "Vous n'avez pas accès à tous les projets de ce client : demandez à une personne qui les voit tous."
const PREVIEW_CHANGED = "La situation a changé depuis l'aperçu (une facture vient peut-être d'être émise). Relisez l'avertissement puis confirmez."

function errorCode(e: unknown): string | null {
  return e instanceof ApiError ? e.code : null
}

/**
 * SOLY-CUST-001 MS-02 — « Fusionner avec un autre client… ». Step 1 picks the right customer (the
 * target), step 2 shows what moves and confirms. The source record is deleted for good; issued
 * documents keep the identity they were sent with.
 *
 * Same contract as DeleteProjectDialog: the content mounts per open (fresh state), the preview
 * loads first, and when it fails there is a fixed text and NO confirm button — merging blind is
 * not offered.
 */
export function MergeCustomerDialog({ open, ...props }: MergeCustomerDialogProps) {
  if (!open) return null
  return <MergeCustomerDialogContent {...props} />
}

function MergeCustomerDialogContent({ sourceId, sourceName, onClose }: Omit<MergeCustomerDialogProps, 'open'>) {
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null)
  // Escape / outside click must not close the dialog while the merge runs: its outcome (and a
  // possible error) would be lost.
  const [busy, setBusy] = useState(false)

  return (
    <AlertDialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <AlertDialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="client-merge-dialog">
        {target === null ? (
          <PickTargetStep sourceId={sourceId} sourceName={sourceName} onPick={setTarget} onClose={onClose} />
        ) : (
          <ConfirmStep sourceId={sourceId} targetId={target.id} onClose={onClose} onBusyChange={setBusy} />
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}

function PickTargetStep({
  sourceId,
  sourceName,
  onPick,
  onClose,
}: {
  sourceId: string
  sourceName: string
  onPick: (target: { id: string; name: string }) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  // includeInactive: the record to keep may be a returning client whose projects are all in the trash,
  // which the ordinary search hides.
  const { results, isLoading, error } = useClientSearchV2({ q: query, take: 20, includeInactive: true })
  // The search hook is shared by three screens: the source is excluded here, not in the hook.
  const candidates = useMemo(() => results.filter((r) => r.customerId && r.customerId !== sourceId), [results, sourceId])

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Fusionner « {sourceName} » dans…</AlertDialogTitle>
        <AlertDialogDescription>Choisissez la fiche à garder.</AlertDialogDescription>
      </AlertDialogHeader>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Nom, téléphone, adresse du chantier…"
          className="pl-9"
          data-testid="client-merge-search-input"
        />
      </div>

      <div className="max-h-80 space-y-2 overflow-y-auto">
        {isLoading && <p className="text-sm text-muted-foreground">Recherche…</p>}
        {!isLoading && error && (
          <p className="text-sm text-destructive" data-testid="client-merge-search-error">
            La recherche a échoué. Réessayez.
          </p>
        )}
        {!isLoading && !error && query.trim() !== '' && candidates.length === 0 && (
          <p className="text-sm text-muted-foreground">Aucun client trouvé</p>
        )}
        {candidates.map((c) => (
          <CandidateRow key={c.customerId!} client={c} onPick={onPick} />
        ))}
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel onClick={onClose} data-testid="client-merge-cancel-btn">
          Annuler
        </AlertDialogCancel>
      </AlertDialogFooter>
    </>
  )
}

function CandidateRow({ client, onPick }: { client: ClientSearchItemV2; onPick: (t: { id: string; name: string }) => void }) {
  const name = client.name?.trim() || '(Sans nom)'
  const contacts = client.contacts || []
  const phone = (contacts.find((c) => c?.isPrimary) || contacts[0])?.phone
  const site = client.lastJobSites?.[0]
  const siteText = site?.address?.trim() || site?.label?.trim()

  return (
    <button
      type="button"
      onClick={() => onPick({ id: client.customerId!, name })}
      className="w-full rounded-lg border border-border px-4 py-3 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="client-merge-candidate"
    >
      <p className="truncate font-medium">{name}</p>
      <div className="flex flex-wrap gap-x-3 text-sm text-muted-foreground">
        {phone && <span>{phone}</span>}
        {siteText && <span className="truncate">{siteText}</span>}
      </div>
    </button>
  )
}

function ConfirmStep({
  sourceId,
  targetId,
  onClose,
  onBusyChange,
}: {
  sourceId: string
  targetId: string
  onClose: () => void
  onBusyChange: (busy: boolean) => void
}) {
  const navigate = useNavigate()
  const [preview, setPreview] = useState<CustomerMergePreviewV2 | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewVersion, setPreviewVersion] = useState(0)
  const [understood, setUnderstood] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    let disposed = false
    getClientMergePreviewV2(sourceId, targetId)
      .then((dto) => {
        if (!disposed) setPreview(dto)
      })
      .catch((e: unknown) => {
        if (disposed) return
        setPreview(null)
        setPreviewError(
          errorCode(e) === 'MERGE_PROJECTS_NOT_ACCESSIBLE'
            ? NO_PROJECT_ACCESS
            : 'Impossible de préparer la fusion. Fermez cette fenêtre et réessayez.',
        )
      })
    return () => {
      disposed = true
    }
  }, [sourceId, targetId, previewVersion])

  // D7 — warnings, never a block: « J'ai compris » is required only when one of them shows. The
  // identity warning stands on its own: two different legal entities must never merge on one click,
  // invoices or not.
  const showInvoiceWarning = (preview?.uncreditedInvoiceCount ?? 0) > 0
  const showIdentityWarning = preview?.identityDiffers === true
  const needsAcknowledgement = showInvoiceWarning || showIdentityWarning
  const canSubmit = preview !== null && !isSubmitting && (!needsAcknowledgement || understood)

  const submit = async () => {
    if (!preview || !canSubmit) return
    setIsSubmitting(true)
    onBusyChange(true)
    setSubmitError(null)
    try {
      await mergeClientV2(sourceId, { targetId, acknowledgeWarnings: needsAcknowledgement && understood })
      showSuccessToast('Fiches fusionnées')
      onClose()
      navigate(`/clients/${targetId}`)
    } catch (e: unknown) {
      // The dialog stays open. A warning that appeared since the preview (an invoice issued meanwhile)
      // reloads it, so the user reads and acknowledges what the server now sees.
      const code = errorCode(e)
      if (code === 'MERGE_REQUIRES_ACKNOWLEDGEMENT') {
        setUnderstood(false)
        // Cleared first: until the new preview lands, nothing can be submitted on the old one.
        setPreview(null)
        setPreviewVersion((v) => v + 1)
        setSubmitError(PREVIEW_CHANGED)
      } else {
        setSubmitError(code === 'MERGE_PROJECTS_NOT_ACCESSIBLE' ? NO_PROJECT_ACCESS : MERGE_FAILED)
      }
      setIsSubmitting(false)
      onBusyChange(false)
    }
  }

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {preview ? `Fusionner « ${preview.source.name} » dans « ${preview.target.name} »` : 'Fusionner les fiches'}
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div>
            {preview === null && !previewError && <p>Chargement de l'aperçu…</p>}
            {previewError && <p data-testid="client-merge-preview-error">{previewError}</p>}
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>

      {preview && (
        <div className="space-y-4 text-sm" data-testid="client-merge-preview">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <IdentityBlock title="Sera supprimé" identity={preview.source} />
            <IdentityBlock title="Garde tout" identity={preview.target} />
          </div>

          <p data-testid="client-merge-counts">
            {describeMovedItems(preview)} vont sur « {preview.target.name} ».
          </p>
          {preview.contactsNotCopiedCount > 0 && (
            <p className="text-muted-foreground" data-testid="client-merge-contacts-not-copied">
              {describeNotCopied(preview.contactsNotCopiedCount)}
            </p>
          )}
          <p className="text-muted-foreground">
            La fiche « {preview.source.name} » sera supprimée définitivement. Les factures déjà émises restent telles
            qu'elles ont été envoyées.
          </p>

          {needsAcknowledgement && (
            <div
              className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900"
              data-testid="client-merge-warning"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="space-y-1">
                  {showInvoiceWarning && (
                    <p data-testid="client-merge-invoice-warning">
                      « {preview.source.name} » a {plural(preview.uncreditedInvoiceCount, 'facture non créditée', 'factures non créditées')}.
                      Si l'une d'elles doit être annulée, faites la note de crédit <strong>avant</strong> de fusionner :
                      après, elle porterait les coordonnées de « {preview.target.name} ».
                    </p>
                  )}
                  {showIdentityWarning && (
                    <p className="font-semibold" data-testid="client-merge-identity-warning">
                      Les deux fiches n'ont pas la même identité ({identityLabel(preview.source)} /{' '}
                      {identityLabel(preview.target)}). Vérifiez avec votre comptable avant de fusionner.
                    </p>
                  )}
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2">
                <Checkbox
                  checked={understood}
                  onCheckedChange={(v) => setUnderstood(v === true)}
                  data-testid="client-merge-understood-checkbox"
                />
                <span>J'ai compris</span>
              </label>
            </div>
          )}

          {submitError && (
            <p className="text-destructive" role="alert" data-testid="client-merge-submit-error">
              {submitError}
            </p>
          )}
        </div>
      )}

      <AlertDialogFooter>
        <AlertDialogCancel onClick={onClose} disabled={isSubmitting} data-testid="client-merge-cancel-btn">
          Annuler
        </AlertDialogCancel>
        {preview && (
          <Button onClick={submit} disabled={!canSubmit} data-testid="client-merge-confirm-btn">
            {isSubmitting ? 'Fusion…' : 'Fusionner'}
          </Button>
        )}
      </AlertDialogFooter>
    </>
  )
}

function IdentityBlock({ title, identity }: { title: string; identity: CustomerMergeIdentityV2 }) {
  return (
    <div className="rounded-md border bg-muted/40 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="mt-1 font-medium">{identity.name || '(Sans nom)'}</p>
      {identity.vatNumber?.trim() && <p className="text-muted-foreground">TVA {identity.vatNumber}</p>}
    </div>
  )
}

function plural(n: number, singular: string, pluralForm: string): string {
  return `${n} ${n === 1 ? singular : pluralForm}`
}

/** « 3 projets (dont 1 à la corbeille) · 2 devis · … » — zero counts are left out. */
function describeMovedItems(p: CustomerMergePreviewV2): string {
  const parts: string[] = []
  if (p.projectCount > 0) {
    const trashed = p.deletedProjectCount > 0 ? ` (dont ${p.deletedProjectCount} à la corbeille)` : ''
    parts.push(plural(p.projectCount, 'projet', 'projets') + trashed)
  }
  if (p.quoteVersionCount > 0) parts.push(plural(p.quoteVersionCount, 'devis', 'devis'))
  if (p.invoiceCount > 0) parts.push(plural(p.invoiceCount, 'facture', 'factures'))
  if (p.creditNoteCount > 0) parts.push(plural(p.creditNoteCount, 'note de crédit', 'notes de crédit'))
  if (p.contactsToCopyCount > 0) parts.push(plural(p.contactsToCopyCount, 'contact recopié', 'contacts recopiés'))
  return parts.length > 0 ? parts.join(' · ') : 'Aucun projet ni document'
}

/** « 1 contact déjà présent n'est pas recopié » — said plainly, since those contacts are deleted. */
function describeNotCopied(n: number): string {
  return n === 1 ? "1 contact déjà présent (même e-mail ou téléphone) n'est pas recopié." : `${n} contacts déjà présents (même e-mail ou téléphone) ne sont pas recopiés.`
}

function identityLabel(identity: CustomerMergeIdentityV2): string {
  const vat = identity.vatNumber?.trim()
  if (vat) return vat
  return identity.type === 2 ? 'société sans TVA' : 'particulier'
}
